import { Moon, Sunrise, Dice1 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import type { WidgetAction } from "@/lib/mechanics/types";
import { useLocale } from "@/hooks/useLocale";

interface RestWidgetProps {
  hit_dice_current?: number;
  hit_dice_max?: number;
  last_long_rest_day?: number;
  game_day?: number;
  widgetActions?: WidgetAction[];
  onWidgetAction?: (action: WidgetAction, params: Record<string, string>) => void;
}

export function RestWidget({
  hit_dice_current = 0,
  hit_dice_max = 1,
  last_long_rest_day = 0,
  game_day = 1,
  widgetActions,
  onWidgetAction,
}: RestWidgetProps) {
  const { t } = useLocale();
  const pct = hit_dice_max > 0 ? (hit_dice_current / hit_dice_max) * 100 : 0;
  const daysSinceRest = game_day - last_long_rest_day;

  return (
    <div className="space-y-2 p-2 rounded-lg bg-card border border-border text-[11px]">
      <div className="flex items-center gap-1.5 font-semibold text-xs text-foreground">
        <Moon className="w-3.5 h-3.5 text-primary" />
        {t.widget_rest_title}
      </div>

      {/* Hit Dice */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-muted-foreground">
          <span className="flex items-center gap-1">
            <Dice1 className="w-3 h-3" /> {t.widget_rest_hit_dice}
          </span>
          <span className="font-mono text-foreground">{hit_dice_current}/{hit_dice_max}</span>
        </div>
        <Progress value={pct} className="h-1.5" />
      </div>

      {/* Last long rest */}
      {last_long_rest_day > 0 && (
        <div className="flex items-center justify-between text-muted-foreground">
          <span>{t.widget_rest_last_long}</span>
          <Badge variant={daysSinceRest > 1 ? "destructive" : "secondary"} className="text-[9px] px-1.5 py-0">
            {daysSinceRest === 0 ? t.widget_rest_today : `${daysSinceRest}${t.widget_rest_days_ago}`}
          </Badge>
        </div>
      )}

      {/* Rest action buttons */}
      <div className="flex gap-1 pt-1">
        <Button
          size="sm"
          variant="outline"
          className="flex-1 h-6 text-[10px] gap-1"
          onClick={() => onWidgetAction?.({ id: "short_rest", label: t.widget_rest_short, messageTemplate: "[SHORT_REST]" } as WidgetAction, {})}
        >
          <Sunrise className="w-3 h-3" /> {t.widget_rest_short}
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="flex-1 h-6 text-[10px] gap-1"
          onClick={() => onWidgetAction?.({ id: "long_rest", label: t.widget_rest_long, messageTemplate: "[LONG_REST]" } as WidgetAction, {})}
        >
          <Moon className="w-3 h-3" /> {t.widget_rest_long}
        </Button>
      </div>
    </div>
  );
}

/** Compact status bar version */
export function RestStatus({
  hit_dice_current = 0,
  hit_dice_max = 1,
}: Pick<RestWidgetProps, "hit_dice_current" | "hit_dice_max">) {
  if (hit_dice_max <= 0) return null;
  return (
    <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
      <Dice1 className="w-3 h-3" />
      <span className="font-mono">{hit_dice_current}/{hit_dice_max}</span>
    </div>
  );
}
